import { FC } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import SvgInformation from '@app/shared/icons/Information';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastTranche,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { translationKeys } from '@locale/setupI18n';
import { getProceedsCapToolTipContent } from '@app/shared/helpers/proceeds-cap/proceeds-cap';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

type ValueKeys = Omit<FirstLastTranche, 'conditions' | 'ordinaryEquityMetrics'>;

interface TrancheCalculationRowProps {
  valueKey: keyof ValueKeys;
  labelKey: translationKeys;
  trancheIndex: number;
  upper?: boolean;
  percentage?: boolean;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  selectedInstrument: string;
  waterfallIterations: number;
  getTrancheDictionary: (firstOrLastFive: 'first' | 'last') => Dictionary<FirstLastTranche>[];
  l: (key: translationKeys) => string;
}

const TrancheCalculationRow: FC<TrancheCalculationRowProps> = ({
  valueKey,
  labelKey,
  trancheIndex,
  upper = false,
  percentage = false,
  opmWaterfall,
  selectedYear,
  selectedInstrument,
  waterfallIterations,
  getTrancheDictionary,
  l,
}) => {
  const tranches = opmWaterfall[selectedYear].first[1].instruments[selectedInstrument].tranches;
  const trancheNames = Object.keys(tranches);

  if (trancheNames.length === 0 || tranches[trancheNames[trancheIndex]][valueKey] === null) {
    return null;
  }

  const tranche = tranches[trancheNames[trancheIndex]];
  const hasMultipleConditions = tranche.conditions.length > 1;

  if (valueKey === 'activeHurdle' && !hasMultipleConditions) {
    return null;
  }

  const thisTrancheName = trancheNames[trancheIndex];
  const firstValues = getTrancheDictionary('first').map((dict) => dict[thisTrancheName]);
  const lastValues = getTrancheDictionary('last').map((dict) => dict[thisTrancheName]);

  const combinedValues = [...(firstValues || []), ...(lastValues || [])];

  const toolTipContent = getProceedsCapToolTipContent(
    valueKey,
    combinedValues.map((tranche) => tranche.hasHitCap ?? false),
    combinedValues.map((tranche) => tranche.hasReceivedEnhancedParticipation ?? false)
  );

  return (
    <tr
      key={`tranche-row-${trancheIndex}-${valueKey}`}
      data-testid={`${valueKey}-tranche${trancheIndex + 1}-title`}>
      <td
        className={classNames(styles['border-right'], {
          [styles['tooltip-header']]: toolTipContent,
        })}>
        {l(labelKey)}
        {toolTipContent && (
          <Tooltip content={toolTipContent} placement={TooltipPlacement.Right}>
            <Button
              appearance={ButtonAppearance.CLEAN}
              size={ButtonSize.FLUID}
              aria-label={toolTipContent}>
              <SvgInformation className={styles['warning-icon']} />
            </Button>
          </Tooltip>
        )}
      </td>
      {[...firstValues, ...lastValues].map((tranche, index) => {
        const value = tranche[valueKey];
        return (
          <td
            key={`value-${trancheIndex}-${index}`}
            className={classNames({
              [styles['border-right']]: index + 1 === waterfallIterations,
            })}
            data-testid={`${valueKey}-tranche${trancheIndex + 1}-simulation-${index}`}>
            <CellValue
              topLine={valueKey === 'trancheDistribution' ? tranche.hasHitCap ?? false : false}
              plusSign={
                valueKey === 'trancheParticipationPc'
                  ? tranche.hasReceivedEnhancedParticipation ?? false
                  : false
              }
              className={
                upper
                  ? value === false
                    ? styles['upper-text-value-false']
                    : styles['upper-text']
                  : ''
              }
              value={
                percentage && typeof value === 'number'
                  ? getCalculatedProjectValueInPercent(value)
                  : value
              }
              {...(percentage ? enteredPercentageViewRules : amountRules)}
            />
          </td>
        );
      })}
    </tr>
  );
};

export default TrancheCalculationRow;
